import React from 'react';
import { Descriptions, Tag, Typography, Divider } from 'antd';
import type { IndustryNode } from '../types';

interface Props {
    node: IndustryNode;
    onClose: () => void;
}

const { Title, Paragraph } = Typography;

const ChainNodeDetails: React.FC<Props> = ({ node, onClose }) => {
    const { attributes } = node;

    // HUD Style container
    const hudStyle: React.CSSProperties = {
        padding: '24px',
        color: '#e0f7fa',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: '20px',
        background: 'rgba(0, 50, 60, 0.4)',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid rgba(0, 229, 255, 0.2)'
    };

    const labelStyle: React.CSSProperties = {
        color: 'rgba(0, 229, 255, 0.7)',
        fontSize: '12px',
        marginBottom: '4px'
    };

    const valueStyle: React.CSSProperties = {
        color: '#fff',
        fontSize: '16px',
        fontWeight: 500
    };

    const renderAttributes = () => {
        if (!attributes) return null;

        return (
            <div style={sectionStyle}>
                {Object.entries(attributes).map(([key, value]) => {
                    if (key === 'description' || key === 'location') return null; // Handle separately
                    let label = key;
                    if (key === 'reserves') label = '探明储量';
                    if (key === 'capacity') label = '年产能';
                    if (key === 'price') label = '当前价格';
                    if (key === 'share') label = '市场份额';
                    if (key === 'growth') label = '增长率';
                    if (key === 'key_player') label = '主要玩家';

                    return (
                        <div key={key} style={{ marginBottom: '12px' }}>
                            <div style={labelStyle}>{label}</div>
                            <div style={valueStyle}>{value}</div>
                        </div>
                    );
                })}
                {attributes.location && (
                    <div style={{ marginBottom: '12px' }}>
                        <div style={labelStyle}>地理位置</div>
                        <div style={valueStyle}>{attributes.location}</div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={hudStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Title level={3} style={{ color: '#00e5ff', margin: 0, textShadow: '0 0 10px rgba(0,229,255,0.5)' }}>
                    {node.name}
                </Title>
                <div onClick={onClose} style={{ cursor: 'pointer', fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>✕</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <Tag color={node.type === 'mining' ? 'gold' : node.type === 'smelting' ? 'orange' : 'cyan'} style={{ padding: '4px 12px', fontSize: '14px' }}>
                    {node.type === 'mining' ? '上游 · 矿产' : node.type === 'smelting' ? '中游 · 加工' : '下游 · 应用'}
                </Tag>
            </div>

            <Divider style={{ borderColor: 'rgba(0, 229, 255, 0.3)' }} />

            {node.market_size && (
                <div style={{ ...sectionStyle, background: 'rgba(0, 229, 255, 0.1)', border: '1px solid #00e5ff' }}>
                    <div style={labelStyle}>
                        {node.type === 'mining' ? '产能规模' : node.type === 'application' ? '市场占比' : '总产量'}
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'monospace', color: '#00e5ff' }}>
                        {node.market_size.toLocaleString()}
                        <span style={{ fontSize: '14px', marginLeft: '6px', color: 'rgba(255,255,255,0.7)' }}>
                            {node.type === 'application' ? '%' : '吨/年'}
                        </span>
                    </div>
                </div>
            )}

            {renderAttributes()}

            {attributes?.description && (
                <div style={{ marginTop: 'auto', background: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px' }}>
                    <div style={{ ...labelStyle, marginBottom: '8px' }}>详细描述</div>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', lineHeight: '1.6' }}>
                        {attributes.description}
                    </Paragraph>
                </div>
            )}
        </div>
    );
};

export default ChainNodeDetails;
